/* eslint-disable react/no-unknown-property */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';
import { tableStore } from '../../../stores/table-store';

const DRAG_SCROLL_EDGE_THRESHOLD = 60;
const DRAG_SCROLL_SPEED = 8;

export const DragShadowPortal = observer(() => {
  // Hooks must come before any conditional returns
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const dragStartY = useRef<number | null>(null);
  const scrollAnimationRef = useRef<number | null>(null);

  const { dragSession } = tableStore;
  // Render the bundle shadow for both genuine multi-drags AND single
  // group-row drags. The latter carry isMulti=false (so handleDragMove
  // and handleDragEnd preserve single-row semantics and route through
  // moveRowGroup) but a non-zero groupHeight that represents the visible
  // bundle (group row + visible subRows) — PSTUDIO-3063 #5.
  // isBundleShadowActive intentionally derives from `groupHeight > 0` rather
  // than a named `isBundle` flag — adding that flag requires a
  // startDragSession signature change in table-store.ts and is out of scope
  // here. If a future regression sets groupHeight=0 for a real multi-drag
  // (e.g. virtualised remount with stale rowRefs), the shadow will silently
  // stop rendering for multi-drags too. PSTUDIO-3063 #5 follow-up tracks
  // promoting this to a named flag.
  const isBundleShadowActive = !!(
    dragSession?.activeRowId && dragSession.groupHeight > 0
  );
  const isOversizeDrag = dragSession?.isOversizeDrag ?? false;

  useEffect(() => {
    if (!isBundleShadowActive || !isOversizeDrag) return;

    const scrollContainer = document.querySelector('.c-editor-table-scroller');
    if (!scrollContainer) return;

    const preventScroll = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    scrollContainer.addEventListener('wheel', preventScroll, {
      passive: false,
    });
    scrollContainer.addEventListener('touchmove', preventScroll, {
      passive: false,
    });

    return () => {
      scrollContainer.removeEventListener('wheel', preventScroll);
      scrollContainer.removeEventListener('touchmove', preventScroll);
    };
  }, [isBundleShadowActive, isOversizeDrag]);

  useEffect(() => {
    if (!isBundleShadowActive) {
      setDragOffsetY(0);
      dragStartY.current = null;
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
      return;
    }

    const scrollContainer = document.querySelector('.c-editor-table-scroller');
    let lastPointerY = 0;

    const autoScroll = () => {
      if (!scrollContainer || !isBundleShadowActive || isOversizeDrag) return;

      const rect = scrollContainer.getBoundingClientRect();
      const distanceFromTop = lastPointerY - rect.top;
      const distanceFromBottom = rect.bottom - lastPointerY;

      let scrollDelta = 0;
      if (
        distanceFromTop < DRAG_SCROLL_EDGE_THRESHOLD &&
        scrollContainer.scrollTop > 0
      ) {
        scrollDelta =
          -DRAG_SCROLL_SPEED *
          (1 - distanceFromTop / DRAG_SCROLL_EDGE_THRESHOLD);
      } else if (
        distanceFromBottom < DRAG_SCROLL_EDGE_THRESHOLD &&
        scrollContainer.scrollTop <
          scrollContainer.scrollHeight - scrollContainer.clientHeight
      ) {
        scrollDelta =
          DRAG_SCROLL_SPEED *
          (1 - distanceFromBottom / DRAG_SCROLL_EDGE_THRESHOLD);
      }

      if (scrollDelta !== 0) scrollContainer.scrollTop += scrollDelta;
      scrollAnimationRef.current = requestAnimationFrame(autoScroll);
    };

    const handlePointerMove = (e: PointerEvent) => {
      lastPointerY = e.clientY;

      if (dragStartY.current === null) {
        dragStartY.current = e.clientY;
        if (!isOversizeDrag) {
          scrollAnimationRef.current = requestAnimationFrame(autoScroll);
        }
      }

      if (!isOversizeDrag) {
        setDragOffsetY(e.clientY - dragStartY.current);
      }
    };

    document.addEventListener('pointermove', handlePointerMove);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
    };
  }, [isBundleShadowActive, isOversizeDrag]);

  // Early return after all hooks
  if (!dragSession || !isBundleShadowActive) return null;

  const shadowHeight = dragSession.groupHeight;
  let shadowTop = 0;
  let shadowLeft = 0;
  let shadowWidth = 0;
  let shadowPosition: 'fixed' | 'absolute' = 'fixed';

  // Declared here so it's available for the portal target below
  const scrollContainer = document.querySelector('.c-editor-table-scroller');

  if (isOversizeDrag) {
    shadowPosition = 'absolute';
    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const firstSelectedId = dragSession.selectedRowIds[0];
      const firstRowEl = firstSelectedId
        ? document.querySelector(`[data-rowid="${firstSelectedId}"]`)
        : null;
      if (firstRowEl) {
        const rowRect = firstRowEl.getBoundingClientRect();
        shadowTop = rowRect.top - containerRect.top + scrollContainer.scrollTop;
        shadowLeft = rowRect.left - containerRect.left;
        shadowWidth = rowRect.width;
      }
    }
  } else {
    shadowTop = dragSession.initialTop + dragOffsetY;
    shadowLeft = dragSession.initialLeft;
    shadowWidth = dragSession.initialWidth;
    const viewportHeight = window.innerHeight;
    shadowTop = Math.max(
      10,
      Math.min(shadowTop, viewportHeight - shadowHeight - 10)
    );
  }

  return createPortal(
    <div
      className="c-editor-table-tr__drag-shadow"
      test-id="c-editor-table__drag-shadow"
      style={{
        position: shadowPosition,
        top: shadowTop,
        left: isOversizeDrag ? shadowLeft : dragSession.initialLeft,
        width: isOversizeDrag ? shadowWidth : dragSession.initialWidth,
        height: shadowHeight,
        backgroundColor: 'rgba(139, 92, 246, 0.15)',
        border: '2px solid var(--ps-colors-purple-500, #8b5cf6)',
        borderRadius: '4px',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    />,
    isOversizeDrag && scrollContainer ? scrollContainer : document.body
  );
});
